/*
 * DecoNova — Pack Selector (Custom Element)
 * <deconova-pack-selector> lit son <script data-dn-config> JSON,
 * rend les cartes de pack + slots, gère le add-to-cart.
 *
 * Dépend de deconova-shared.js (chargé globalement via stylesheets/scripts).
 * i18n : libellés via window.DecoNova.t (window.DN_I18N), repli français.
 * L'init attend que window.DN_I18N soit disponible (max ~1 s) avant le premier rendu.
 */

(function () {
  'use strict';

  // S'assurer que les helpers sont chargés (deconova-shared.js)
  function ready() {
    return typeof window.DecoNova === 'object';
  }

  // Petit interval de secours au cas où le shared n'est pas encore prêt
  function ensureShared(cb) {
    if (ready()) return cb();
    let tries = 0;
    const t = setInterval(function () {
      if (ready()) {
        clearInterval(t);
        cb();
      } else if (++tries > 40) {
        clearInterval(t);
        console.error('[DecoNova] deconova-shared.js not loaded — pack selector aborted');
      }
    }, 50);
  }

  // Helpers locaux
  const $  = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => Array.from((ctx || document).querySelectorAll(sel));
  const T  = (k, fb) => window.DecoNova.t(k, fb);
  const F  = (tpl, vars) => window.DecoNova.fill(tpl, vars);

  function interpolate(tpl, vars) {
    return String(tpl || '').replace(/%(\w+)%/g, (_, k) => (vars[k] != null ? vars[k] : ''));
  }

  class DeconovaPackSelector extends HTMLElement {
    connectedCallback() {
      ensureShared(() => this.waitForChildrenThenInit());
    }

    // connectedCallback fire avant que les enfants soient parsés.
    // On attend (via RAF) que le <script data-dn-config> apparaisse,
    // et que le dictionnaire i18n (snippet dn-i18n) soit chargé — max ~1 s.
    waitForChildrenThenInit() {
      let tries = 0;
      const tick = () => {
        const hasCfg = !!this.querySelector('script[data-dn-config]');
        const hasI18n = !!window.DN_I18N;
        if (hasCfg && (hasI18n || tries >= 60)) {
          if (!hasI18n) console.warn('[DecoNova][Pack] window.DN_I18N absent — libellés de repli (FR)');
          this.init();
        } else if (++tries < 120) {
          requestAnimationFrame(tick);
        } else {
          console.error('[DecoNova][Pack] Pas de <script data-dn-config> trouvé après 120 frames');
        }
      };
      tick();
    }

    init() {
      const configEl = $('script[data-dn-config]', this);
      if (!configEl) return;

      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (e) {
        console.error('[DecoNova] Config JSON invalide', e, configEl.textContent);
        return;
      }

      this.colors = (this.config.colors || [])
        .filter(c => c.isActive !== false && c.hex)
        .sort((a, b) => (a.position || 999) - (b.position || 999));

      // Détection robuste du pack_size : extrait le premier nombre du titre
      // ("1 Boîte" → 1, "Pack de 2" → 2, "3 pcs" → 3, "Boîte" → 1 par défaut position)
      this.variantsByPack = {};
      (this.config.variants || [])
        .sort((a, b) => (a.position || 0) - (b.position || 0))
        .forEach((v, idx) => {
          const src = String(v.option1 || v.title || '');
          const m = src.match(/\d+/);
          let ps = m ? parseInt(m[0], 10) : (idx + 1); // fallback : ordre de la variante
          if (ps < 1 || ps > 9) ps = idx + 1;
          if (v.available !== false && !this.variantsByPack[ps]) {
            this.variantsByPack[ps] = Object.assign({}, v, { pack_size: ps });
          }
        });

      this.packSizes = Object.keys(this.variantsByPack)
        .map(Number)
        .sort((a, b) => a - b);

      if (!this.packSizes.length) {
        this.innerHTML = '<p class="dn-lead">' + T('product_unavailable', 'Produit indisponible.') + '</p>';
        return;
      }

      // État
      this.state = {
        selectedPack: this.packSizes[1] || this.packSizes[0], // pré-sélectionne pack 2 si dispo (meilleur défaut commercial)
        slots: [], // [{ mode: 'mono'|'bi', top: colorObj|null, bottom: colorObj|null }]
      };

      this.packGrid = $('[data-dn-pack-grid]', this);
      this.slotsEl  = $('[data-dn-slots]', this);
      this.cta      = $('[data-dn-cta]', this);
      this.ctaLbl   = $('[data-dn-cta-label]', this);

      this.renderPackCards();
      this.ensureSlots();
      this.renderSlots();
      this.updateCta();

      this.cta.addEventListener('click', () => this.handleAddToCart());
    }

    // ───── Rendu des cartes de pack ──────────────────────────────

    renderPackCards() {
      this.packGrid.innerHTML = '';

      const basePack = this.variantsByPack[1];
      const baseUnitPrice = basePack ? basePack.price : null;

      this.packSizes.forEach(ps => {
        const variant = this.variantsByPack[ps];
        const card = document.createElement('button');
        card.type = 'button';
        card.className = 'dn-pack-card';
        card.setAttribute('role', 'radio');
        card.setAttribute('aria-checked', String(ps === this.state.selectedPack));
        card.setAttribute('aria-pressed', String(ps === this.state.selectedPack));
        card.dataset.dnPack = String(ps);

        const unitPriceCents = variant.price / ps;
        const unitPriceFmt = window.DecoNova.formatMoney(unitPriceCents, this.config.locale, this.config.currency);
        const packPriceFmt = window.DecoNova.formatMoney(variant.price, this.config.locale, this.config.currency);

        // Calcul % économie vs prix unitaire du pack 1
        let savingHtml = '';
        if (baseUnitPrice && ps > 1) {
          const fullPrice = baseUnitPrice * ps;
          const pct = Math.round(((fullPrice - variant.price) / fullPrice) * 100);
          if (pct > 0) {
            const savingLabel = interpolate(this.config.labels.savingLabel, { percent: '-' + pct + '%' });
            savingHtml = `<span class="dn-pack-card__saving">${savingLabel}</span>`;
          }
        }

        const coverLabel = F(T(ps > 1 ? 'cover_other' : 'cover_one', ps > 1 ? '%count% cache-boîtes' : '%count% cache-boîte'), { count: ps });

        card.innerHTML = `
          ${savingHtml}
          <span class="dn-pack-card__eyebrow">${variant.title}</span>
          <span class="dn-pack-card__title">${coverLabel}</span>
          <div class="dn-pack-card__price-row">
            <span class="dn-pack-card__price">${packPriceFmt}</span>
            ${ps > 1 ? `<span class="dn-pack-card__unit-price">${interpolate(this.config.labels.unitPriceLabel, { price: unitPriceFmt })}</span>` : ''}
          </div>
        `;

        card.addEventListener('click', () => this.selectPack(ps));
        this.packGrid.appendChild(card);
      });
    }

    // ───── Sélection d'un pack ─────────────────────────────

    selectPack(ps) {
      if (!this.variantsByPack[ps]) return;
      this.state.selectedPack = ps;
      $$('[data-dn-pack]', this.packGrid).forEach(el => {
        const active = Number(el.dataset.dnPack) === ps;
        el.classList.toggle('is-selected', active);
        el.setAttribute('aria-checked', String(active));
        el.setAttribute('aria-pressed', String(active));
      });
      this.ensureSlots();
      this.renderSlots();
      this.updateCta();
    }

    // ───── Slots : garantit le bon nombre de slots dans l'état ─────

    ensureSlots() {
      const n = this.state.selectedPack;
      while (this.state.slots.length < n) {
        this.state.slots.push({ mode: 'mono', top: null, bottom: null });
      }
      this.state.slots = this.state.slots.slice(0, n);
    }

    // ───── Rendu des slots ─────────────────────────────────

    renderSlots() {
      this.slotsEl.hidden = false;
      this.slotsEl.innerHTML = '';

      this.state.slots.forEach((slot, idx) => {
        const slotEl = document.createElement('div');
        slotEl.className = 'dn-slot';
        slotEl.dataset.slotIndex = String(idx);

        const header = document.createElement('div');
        header.className = 'dn-slot__header';

        const title = document.createElement('span');
        title.className = 'dn-slot__index';
        title.textContent = `${this.config.labels.itemLabel} ${idx + 1}`;

        const modeToggle = document.createElement('div');
        modeToggle.className = 'dn-slot__mode-toggle';
        modeToggle.setAttribute('role', 'tablist');

        ['mono', 'bi'].forEach(mode => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'dn-slot__mode-btn';
          btn.setAttribute('aria-pressed', String(slot.mode === mode));
          btn.textContent = mode === 'mono' ? this.config.labels.modeMono : this.config.labels.modeBi;
          btn.addEventListener('click', () => this.setSlotMode(idx, mode));
          modeToggle.appendChild(btn);
        });

        header.appendChild(title);
        header.appendChild(modeToggle);
        slotEl.appendChild(header);

        // Picker(s)
        slotEl.appendChild(this.buildPicker(idx, 'top'));
        if (slot.mode === 'bi') {
          slotEl.appendChild(this.buildPicker(idx, 'bottom'));
        }

        this.slotsEl.appendChild(slotEl);
      });
    }

    buildPicker(slotIdx, role) {
      const slot = this.state.slots[slotIdx];
      const wrap = document.createElement('div');
      wrap.className = 'dn-slot__picker';

      const label = document.createElement('span');
      label.className = 'dn-slot__picker-label';
      if (slot.mode === 'bi') {
        label.textContent = role === 'top' ? this.config.labels.topLabel : this.config.labels.bottomLabel;
      } else {
        label.textContent = T('color', 'Couleur');
      }
      wrap.appendChild(label);

      const grid = document.createElement('div');
      grid.className = 'dn-swatch-grid';
      grid.setAttribute('role', 'radiogroup');
      grid.setAttribute('aria-label', label.textContent);

      const currentHandle = role === 'top'
        ? (slot.top    && slot.top.handle)
        : (slot.bottom && slot.bottom.handle);

      this.colors.forEach(color => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'dn-swatch';
        btn.setAttribute('role', 'radio');
        btn.setAttribute('aria-label', color.name);
        const selected = color.handle === currentHandle;
        btn.setAttribute('aria-pressed', String(selected));
        btn.setAttribute('aria-checked', String(selected));
        btn.title = color.name;
        btn.innerHTML = `
          <span class="dn-swatch__fill dn-swatch__fill--top"    style="background:${color.hex}"></span>
          <span class="dn-swatch__fill dn-swatch__fill--bottom" style="background:${color.hex}"></span>
        `;
        btn.addEventListener('click', () => this.setSlotColor(slotIdx, role, color));
        grid.appendChild(btn);
      });

      wrap.appendChild(grid);
      return wrap;
    }

    // ───── Mutations d'état ────────────────────────────────

    setSlotMode(idx, mode) {
      const slot = this.state.slots[idx];
      slot.mode = mode;
      if (mode === 'mono') {
        slot.bottom = null;
      }
      this.renderSlots();
      this.updateCta();
    }

    setSlotColor(idx, role, color) {
      const slot = this.state.slots[idx];
      if (role === 'top') {
        slot.top = color;
        if (slot.mode === 'mono') slot.bottom = color;
      } else {
        slot.bottom = color;
      }
      this.renderSlots();
      this.updateCta();
    }

    // ───── CTA state ───────────────────────────────────────

    isComplete() {
      return this.state.slots.every(s => s.top && (s.mode === 'mono' ? true : s.bottom));
    }

    updateCta() {
      const variant = this.variantsByPack[this.state.selectedPack];
      const complete = this.isComplete();
      this.cta.disabled = !complete;
      if (complete) {
        const priceFmt = window.DecoNova.formatMoney(variant.price, this.config.locale, this.config.currency);
        this.ctaLbl.textContent = interpolate(this.config.labels.defaultCta, { price: priceFmt });
      } else {
        this.ctaLbl.textContent = this.config.labels.ctaIncomplete;
      }
    }

    // ───── Add to cart ─────────────────────────────────────

    handleAddToCart() {
      if (!this.isComplete()) return;
      const variant = this.variantsByPack[this.state.selectedPack];

      const slotsPayload = this.state.slots.map(s => ({
        top:    s.top,
        bottom: s.mode === 'mono' ? s.top : s.bottom,
      }));

      const properties = window.DecoNova.buildPackProperties(
        this.config.format,
        this.state.selectedPack,
        slotsPayload
      );

      this.cta.disabled = true;
      const originalLabel = this.ctaLbl.textContent;
      this.ctaLbl.textContent = T('cart_adding', 'Ajout en cours…');

      window.DecoNova
        .addToCart({ id: variant.id, quantity: 1, properties: properties })
        .then(() => window.DecoNova.notifyCartChanged())
        .then(() => {
          this.ctaLbl.textContent = T('cart_added', '✓ Ajouté au panier');
          // Redirige vers le cart drawer si Tinker en a un, sinon vers /cart
          setTimeout(() => { window.location.href = '/cart'; }, 600);
        })
        .catch(err => {
          console.error('[DecoNova] add to cart failed', err);
          this.cta.disabled = false;
          this.ctaLbl.textContent = originalLabel;
          alert(err.message || T('cart_add_failed', 'Ajout au panier impossible.'));
        });
    }
  }

  if (!customElements.get('deconova-pack-selector')) {
    customElements.define('deconova-pack-selector', DeconovaPackSelector);
  }
})();
